import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { SiteOverviewStore } from '../sites-data/sites.api';
import {
  AppCandidate,
  AppContext,
  AppMemberView,
  AppMembers,
  SiteCatalog,
  accessibleApps,
} from './site-apps.models';

const base = (siteId: string, appCode?: string) =>
  `/api/sites/${encodeURIComponent(siteId)}/apps${appCode ? `/${encodeURIComponent(appCode)}` : ''}`;

/** Le App del cantiere (contracts/api-site-apps.md): catalogo, attivazione, ruoli nell'App. */
@Injectable({ providedIn: 'root' })
export class SiteAppsApi {
  private readonly http = inject(HttpClient);

  catalog(siteId: string): Observable<SiteCatalog> {
    return this.http.get<SiteCatalog>(base(siteId));
  }

  context(siteId: string, appCode: string): Observable<AppContext> {
    return this.http.get<AppContext>(base(siteId, appCode));
  }

  candidates(siteId: string, appCode: string): Observable<readonly AppCandidate[]> {
    return this.http.get<AppCandidate[]>(`${base(siteId, appCode)}/candidates`);
  }

  /** Attiva l'App; l'Amministratore dell'App è chi attiva, se non se ne indica un altro (FR-007). */
  activate(siteId: string, appCode: string, adminUserId: string | null): Observable<unknown> {
    return this.http.post(`${base(siteId, appCode)}/activation`, { adminUserId });
  }

  deactivate(siteId: string, appCode: string): Observable<unknown> {
    return this.http.post(`${base(siteId, appCode)}/deactivation`, { confirm: true });
  }

  reactivate(siteId: string, appCode: string): Observable<unknown> {
    return this.http.post(`${base(siteId, appCode)}/reactivation`, null);
  }

  designateAdmin(siteId: string, appCode: string, userId: string): Observable<unknown> {
    return this.http.post(`${base(siteId, appCode)}/admin-designation`, { userId });
  }

  members(siteId: string, appCode: string): Observable<AppMembers> {
    return this.http.get<AppMembers>(`${base(siteId, appCode)}/members`);
  }

  assign(siteId: string, appCode: string, userId: string, role: string): Observable<AppMemberView> {
    return this.http.put<AppMemberView>(
      `${base(siteId, appCode)}/members/${encodeURIComponent(userId)}`,
      { role },
    );
  }

  removeMember(siteId: string, appCode: string, userId: string): Observable<unknown> {
    return this.http.delete(`${base(siteId, appCode)}/members/${encodeURIComponent(userId)}`);
  }
}

/**
 * Il catalogo del cantiere selezionato, condiviso fra barra laterale, Panoramica e pagina del
 * catalogo: le voci delle App nella navigazione nascono da qui (FR-011).
 */
@Injectable({ providedIn: 'root' })
export class SiteAppsStore {
  private readonly site = inject(SiteOverviewStore);
  private readonly version = signal(0);

  readonly catalog = httpResource<SiteCatalog>(() => {
    const id = this.site.siteId();
    this.version();
    return id ? base(id) : undefined;
  });

  readonly value = computed(() => (this.catalog.hasValue() ? this.catalog.value() : null));
  readonly accessible = computed(() => accessibleApps(this.value()));

  /** L'utente ha un ruolo nell'App attiva del cantiere. */
  canOpen(appCode: string) {
    return computed(() => this.accessible().some((app) => app.code === appCode));
  }

  reload(): void {
    this.version.update((value) => value + 1);
  }
}
